import _getAppName from "../functions/helpers/getAppName.js";
import _convertToPlainTextCustom from "../functions/quill/convertToPlainTextCustom.js";
import _convertToHtml from "../functions/quill/convertToHtml.js";
import _eol from "eol";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _Event from "../classes/event.js";
import _tcLib from "../lib/timecode.js";
const tcLib = _tcLib;
const Event = _Event;
const removeInvalidEvents = _removeInvalidEvents;
const eol = _eol;
const convertToHtml = _convertToHtml;
const convertToPlainTextCustom = _convertToPlainTextCustom;
// const docX = require("docx"); //API/Creator/CLI
import * as docX from "docx"; //CC-Converter-3
const getAppName = _getAppName;
export default {
  decode: function (input, options) {
    let events = [];

    // Remove line breaks and normalize whitespace for easier parsing
    const normalizedInput = input.replace(/\r?\n/g, ' ').replace(/\s+/g, ' ');

    // Regex to match table rows containing timecode, notes, and audio content
    const rowRegex = /<tr[^>]*>.*?<\/tr>/gi;
    const rows = normalizedInput.match(rowRegex) || [];
    rows.forEach(row => {
      // Extract the three columns (th elements) from each row
      const cellRegex = /<th[^>]*>(.*?)<\/th>/gi;
      const cells = [];
      let match;
      while ((match = cellRegex.exec(row)) !== null) {
        cells.push(match[1]);
      }

      // Skip rows that don't have exactly 3 columns or header rows
      if (cells.length !== 3) {
        return;
      }

      // Extract timecode from first column
      const timecodeMatch = cells[0].match(/(\d{2}:\d{2}:\d{2}:\d{2})/);
      if (!timecodeMatch) {
        return;
      }
      const timecode = timecodeMatch[1];
      const notes = cells[1].replace(/<\/p>/gi, '\n').replace(/<[^>]*>/g, '').trim(); // Replace </p> with line breaks, then remove HTML tags

      // console.log(notes);
      const audioContent = cells[2].replace(/<\/p>/gi, '\n').replace(/<strong>/gi, '<b>').replace(/<\/strong>/gi, '</b>').replace(/<(?!\/?b\b)[^>]*>/g, '').trim(); // Replace </p> with line breaks, replace <strong> with <b>, then remove HTML tags except <b> and </b>

      // Create event
      let ccEvent = new Event();
      ccEvent.start = tcLib.tcToSec(timecode, options.frameRate, options.dropFrame);

      // For end time, we'll need to estimate or use the next event's start time
      // For now, set a default duration of 2 seconds
      ccEvent.end = ccEvent.start + 2;

      // Combine notes and audio content for the text field
      let text = '';
      text += audioContent;
      ccEvent.text = convertToHtml(text.trim());
      ccEvent.notes = notes;
      events.push(ccEvent);
    });

    // Set proper end times based on next event start times
    for (let i = 0; i < events.length - 1; i++) {
      events[i].end = events[i + 1].start;
    }

    // Set last event end time to start + 3 seconds if not already set
    if (events.length > 0) {
      const lastEvent = events[events.length - 1];
      if (lastEvent.end <= lastEvent.start) {
        lastEvent.end = lastEvent.start + 3;
      }
    }
    return events;
  },
  encode: async function (eventGroup, options) {
    let output = "";

    // Create table rows for each event
    const tableRows = [];

    // Add header row
    tableRows.push(new docX.TableRow({
      children: [new docX.TableCell({
        children: [new docX.Paragraph({
          children: [new docX.TextRun({
            text: "TC",
            bold: true
          })]
        })],
        width: {
          size: 2000,
          type: docX.WidthType.DXA
        }
      }), new docX.TableCell({
        children: [new docX.Paragraph({
          children: [new docX.TextRun({
            text: "NOTES",
            bold: true
          })]
        })],
        width: {
          size: 3000,
          type: docX.WidthType.DXA
        }
      }), new docX.TableCell({
        children: [new docX.Paragraph({
          children: [new docX.TextRun({
            text: "AUDIO",
            bold: true
          })]
        })],
        width: {
          size: 4000,
          type: docX.WidthType.DXA
        }
      })]
    }));

    // Add rows for each event
    eventGroup.events.forEach(event => {
      const timecode = tcLib.secToTc(event.start, options.frameRate, options.dropFrame);

      // Parse notes and HTML content into formatted paragraphs for DOCX
      const notesParagraphs = parseNotesToParagraphs(event.notes);
      const audioParagraphs = parseHtmlToParagraphs(convertToPlainTextCustom(event.text));
      tableRows.push(new docX.TableRow({
        children: [new docX.TableCell({
          children: [new docX.Paragraph({
            children: [new docX.TextRun({
              text: timecode
            })]
          })],
          width: {
            size: 2000,
            type: docX.WidthType.DXA
          }
        }), new docX.TableCell({
          children: notesParagraphs,
          width: {
            size: 3000,
            type: docX.WidthType.DXA
          }
        }), new docX.TableCell({
          children: audioParagraphs,
          width: {
            size: 4000,
            type: docX.WidthType.DXA
          }
        })]
      }));
    });

    // Helper function to parse plain text notes into DOCX paragraphs
    function parseNotesToParagraphs(notesText) {
      const paragraphs = [];
      if (!notesText || notesText.trim() === '') {
        // Return empty paragraph if no notes
        return [new docX.Paragraph({
          children: [new docX.TextRun({
            text: ''
          })]
        })];
      }

      // Split by line breaks to get individual lines
      const lines = notesText.split('\n').map(line => line.trim());
      for (const line of lines) {
        if (line.length > 0) {
          paragraphs.push(new docX.Paragraph({
            children: [new docX.TextRun({
              text: line
            })]
          }));
        } else {
          // Add empty paragraph for blank lines
          paragraphs.push(new docX.Paragraph({
            children: [new docX.TextRun({
              text: ''
            })]
          }));
        }
      }

      // If no paragraphs were created, add an empty one
      if (paragraphs.length === 0) {
        paragraphs.push(new docX.Paragraph({
          children: [new docX.TextRun({
            text: ''
          })]
        }));
      }
      return paragraphs;
    }

    // Helper function to parse HTML and create DOCX paragraphs with formatting
    function parseHtmlToParagraphs(htmlText) {
      const paragraphs = [];

      // Handle empty or null text
      if (!htmlText || htmlText.trim() === '') {
        return [new docX.Paragraph({
          children: [new docX.TextRun({
            text: ''
          })]
        })];
      }

      // Split by paragraph tags to get individual lines/paragraphs
      const htmlParagraphs = htmlText.replace(/&nbsp;/g, ' ') // Replace &nbsp; with spaces first
      .split(/<\/p>/gi) // Split on closing </p> tags
      .map(p => p.replace(/<p[^>]*>/gi, '').trim()) // Remove opening <p> tags and trim
      .filter(p => p.length > 0); // Remove empty paragraphs

      // If no valid paragraphs after filtering, return empty paragraph
      if (htmlParagraphs.length === 0) {
        return [new docX.Paragraph({
          children: [new docX.TextRun({
            text: ''
          })]
        })];
      }
      for (const htmlParagraph of htmlParagraphs) {
        const textRuns = [];
        const parts = htmlParagraph.split(/(<\/?strong>)/gi);
        let isBold = false;
        for (let i = 0; i < parts.length; i++) {
          const part = parts[i];
          if (part === '<strong>') {
            isBold = true;
          } else if (part === '</strong>') {
            isBold = false;
          } else if (part.trim()) {
            // Clean up any remaining HTML tags and decode entities
            const cleanText = part.replace(/<[^>]*>/g, '') // Remove any other HTML tags
            .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&').trim();
            if (cleanText) {
              textRuns.push(new docX.TextRun({
                text: cleanText,
                bold: isBold
              }));
            }
          }
        }

        // Create a paragraph with the text runs, or an empty paragraph if no text runs
        if (textRuns.length > 0) {
          paragraphs.push(new docX.Paragraph({
            children: textRuns
          }));
        } else {
          paragraphs.push(new docX.Paragraph({
            children: [new docX.TextRun({
              text: ''
            })]
          }));
        }
      }

      // If no paragraphs were created, add an empty one
      if (paragraphs.length === 0) {
        paragraphs.push(new docX.Paragraph({
          children: [new docX.TextRun({
            text: ''
          })]
        }));
      }
      return paragraphs;
    }

    // Create the table
    const table = new docX.Table({
      rows: tableRows,
      width: {
        size: 100,
        type: docX.WidthType.PERCENTAGE
      },
      borders: {
        top: {
          style: docX.BorderStyle.SINGLE,
          size: 1
        },
        bottom: {
          style: docX.BorderStyle.SINGLE,
          size: 1
        },
        left: {
          style: docX.BorderStyle.SINGLE,
          size: 1
        },
        right: {
          style: docX.BorderStyle.SINGLE,
          size: 1
        },
        insideHorizontal: {
          style: docX.BorderStyle.SINGLE,
          size: 1
        },
        insideVertical: {
          style: docX.BorderStyle.SINGLE,
          size: 1
        }
      }
    });

    // Create document header paragraphs
    const headerChildren = [new docX.Paragraph({
      children: [new docX.TextRun({
        text: "*Title*",
        bold: true,
        size: 24
      })],
      alignment: docX.AlignmentType.CENTER
    }), new docX.Paragraph({
      children: [new docX.TextRun({
        text: "EPISODE ####",
        bold: true,
        size: 24
      })],
      alignment: docX.AlignmentType.CENTER
    }), new docX.Paragraph({
      children: [new docX.TextRun({
        text: "SOFT LOCK SCRIPT",
        bold: true,
        size: 24
      })],
      alignment: docX.AlignmentType.CENTER
    }), new docX.Paragraph({
      text: ""
    }), table];
    let doc = new docX.Document({
      creator: getAppName(),
      sections: [{
        properties: {},
        children: headerChildren
      }]
    });
    if (typeof window === 'undefined') {
      output = await docX.Packer.toBuffer(doc);
    } else {
      output = await docX.Packer.toBlob(doc);
    }
    return output;
  },
  preProcess: {
    encode: function (eventGroup, options) {
      return removeInvalidEvents(eventGroup, true);
    },
    decode: function (input) {
      return eol.lf(input);
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup, options) {
      return eventGroup;
    }
  }
};